import {
  type ComponentType,
  type ReactElement,
  FC,
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from 'react'
import * as serviceWorker from '../service-worker-registration'

export type SWContextData = {
  updateAvailable: boolean
  registration?: ServiceWorkerRegistration
  updateAndReload?: () => void
}

const initialContext = { updateAvailable: false }

export const SWContext = createContext<SWContextData>(initialContext)

export const SWProvider = ({
  children,
  value,
}: {
  children: ReactElement
  value: SWContextData
}) => {
  return <SWContext.Provider value={value}>{children}</SWContext.Provider>
}

export const useSW = () => {
  return useContext(SWContext)
}

export const withServiceWorker = <P extends object>(
  Component: ComponentType<P>
): FC<P> => {
  function WithServiceWorker(props: P) {
    const [updateAvailable, setUpdateAvailable] = useState(false)
    const [registration, setRegistration] =
      useState<ServiceWorkerRegistration>()
    const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(
      null
    )

    const updateAndReload = useCallback(() => {
      if (waitingWorker) {
        waitingWorker.postMessage({ type: 'SKIP_WAITING' })
        waitingWorker.addEventListener('statechange', (e: any) => {
          if (e.target?.state === 'activated') {
            window.location.reload()
          }
        })
      }
    }, [waitingWorker])

    function onSWUpdate(reg: ServiceWorkerRegistration) {
      setWaitingWorker(reg.waiting)
      setUpdateAvailable(true)
      setRegistration(reg)
      console.info('PWA: New version available. Will activate on next page load.')
    }

    useEffect(() => {
      serviceWorker.register({ onUpdate: onSWUpdate })
    }, [])

    const contextValue: SWContextData = {
      updateAvailable,
      registration,
      updateAndReload,
    }

    return (
      <SWProvider value={contextValue}>
        <Component {...props} />
      </SWProvider>
    )
  }

  return WithServiceWorker
}
